/**
 * SpoofioDiagram — animated three-frame illustration of the phishing
 * detection loop:
 *   1. "Your Page" card fades in
 *   2. "Fake Page" card slides in (red-tinted)
 *   3. Alert icon appears with a dotted line back to "Your Page"
 *
 * Frames are driven by static Tailwind keyframes (`animate-welcomeFrame*`,
 * `animate-welcomeLoop`) so this component carries no JS animation state.
 *
 * Honors `prefers-reduced-motion: reduce` via the `useReducedMotion` hook —
 * when reduced, all three frames render simultaneously without animation.
 *
 * The `compact` prop renders a smaller, denser variant suited for the
 * /signup hero panel where vertical space is at a premium.
 */

import { useReducedMotion } from '@/hooks/useReducedMotion'

type SpoofioDiagramProps = {
  compact?: boolean
}

function PageIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  )
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  )
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
      />
    </svg>
  )
}

function PageCard({
  label,
  tone,
  compact,
}: {
  label: string
  tone: 'neutral' | 'danger'
  compact?: boolean
}) {
  const toneClasses =
    tone === 'danger'
      ? 'bg-red-50 border-red-200 text-red-700'
      : 'bg-white border-primary-200 text-primary-700'
  const sizeClasses = compact ? 'h-20 w-24 gap-1' : 'h-24 w-32 gap-2'
  const iconSize = compact ? 'h-6 w-6' : 'h-8 w-8'
  return (
    <div
      className={[
        'flex flex-col items-center justify-center rounded-xl border-2 shadow-sm',
        sizeClasses,
        toneClasses,
      ].join(' ')}
    >
      <PageIcon className={iconSize} />
      <span className="text-xs font-medium">{label}</span>
    </div>
  )
}

export function SpoofioDiagram({ compact = false }: SpoofioDiagramProps) {
  const reduced = useReducedMotion()
  const cls = (animClass: string) => (reduced ? '' : animClass)
  const padding = compact ? 'p-4' : 'p-6'
  const maxWidth = compact ? 'max-w-sm' : 'max-w-md'

  return (
    <div
      data-testid="welcome-animation"
      aria-hidden="true"
      className={[
        'mx-auto w-full rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200',
        maxWidth,
        padding,
        cls('animate-welcomeLoop'),
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center justify-between gap-3">
        <div data-testid="welcome-frame-1" className={cls('animate-welcomeFrame1')}>
          <PageCard label="Your Page" tone="neutral" compact={compact} />
        </div>

        <div
          data-testid="welcome-frame-2"
          className={['flex items-center gap-2', cls('animate-welcomeFrame2')]
            .filter(Boolean)
            .join(' ')}
        >
          <ArrowIcon className="h-6 w-6 text-red-400" />
          <PageCard label="Fake Page" tone="danger" compact={compact} />
        </div>
      </div>

      <div
        data-testid="welcome-frame-3"
        className={[
          'mt-4 flex items-center justify-center gap-3 text-primary-700',
          cls('animate-welcomeFrame3'),
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="inline-block h-0.5 w-16 border-t-2 border-dotted border-primary-400" />
        <BellIcon className="h-6 w-6" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          Alert sent
        </span>
      </div>
    </div>
  )
}
